import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';

import {
    PatientAPCard,
    PatientDetailTab,
    PatientNoteCard,
    GoBackButton,
    StyledCard,
    StyledCardContent,
    PersonalInfoForm,
    PersonalHealthForm,
    EmergencyForm,
    OutlinedButton,
    ContainedButton,
    Progress,
    ConfirmDialog,
    Span,
    SyncIcon,
    CheckIcon,
    Text,
} from '../../components/index';

import AppointmentHistory from '../../components/Provider/Patient/AppointmentHistory';
import { alertError, alertSuccess } from '../../redux/stateActions';
import {
    GET_PATIENT,
    UPDATE_PATIENT,
    GET_PRIMARY_PATIENTS,
    GET_NOTES,
    CREATE_PATIENT_NOTE,
    UPDATE_PATIENT_NOTE,
    DELETE_PATIENT_NOTE,
    GET_PATIENT_HISTORY,
    updatePrimaryPatient,
    removePatient,
    syncUserMindGym,
} from '../../services/provider';

import { providerPageWidth, MONTHS } from '../../constant';

import { validEmail, validPhone, specialCharacters } from '../../utils/regex';
import checkID from '../../utils/checkID';

const PatientDetail = ({ match, history, location }) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    const [isDisabled, setIsDisabled] = useState(false);

    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [patientData, setPatientData] = useState({});
    const [selectedTab, setSelectedTab] = useState(0);
    const [pageLoading, setPageLoading] = useState(true);

    const tabsChangeHandler = (event, newValue) => {
        setSelectedTab(newValue);
    };

    useEffect(() => {
        if (!id) {
            history.goBack();
        }
        if (match.path === '/provider/client/detail/:id') {
            setIsEditing(true);
        }

        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 1);
    }, []);

    const goBack = () => {
        if (location.state?.prevPath) {
            const path = {
                pathname: location.state.prevPath,
                state: {
                    tab: location.state.prevTab,
                },
            };
            history.push(path);
        } else {
            history.goBack();
        }
    };

    const [errors, setErrors] = useState([]);

    /*--------------- Personal Info Form ---------------*/
    const [personalInfo, setPersonalInfo] = useState({
        HNCode: '',
        IDCardNumber: '',
        fullname: '',
        gender: '',
        email: '',
        mobileNumber: '',
        birthday: null,
    });

    const handlePersonalChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        let newPersonalInfo = { ...personalInfo };
        newPersonalInfo[name] = value;
        setPersonalInfo(newPersonalInfo);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleDateChange = (event) => {
        let newErrors = [...errors];
        let newPersonalInfo = { ...personalInfo };

        if (event === null) {
            newPersonalInfo.birthday = undefined;
            setPersonalInfo(newPersonalInfo);

            newErrors = newErrors.filter((error) => error.property !== 'birthday');
        } else if (dayjs(event).isValid()) {
            newPersonalInfo.birthday = event.toISOString();
            setPersonalInfo(newPersonalInfo);

            newErrors = newErrors.filter((error) => error.property !== 'birthday');
        } else {
            newErrors.push({
                property: 'birthday',
                message: 'Invalid date.',
            });
        }
        setErrors(newErrors);
    };

    /*--------------- Personal Health Form ---------------*/
    const [personalHealth, setPersonalHealth] = useState({
        weight: 0,
        height: 0,
        drugAllergy: 'none',
        chronicDeseases1: 'none',
        pregnant: false,
        smoker: false,
        drinker: false,
        chronicDeseases2: '',
        familyMedicalRecord: '',
        patientTag: [],
    });

    const handlePersonalHealthChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        let newPersonalHealth = { ...personalHealth };
        if (['pregnant', 'smoker', 'drinker'].includes(name)) {
            newPersonalHealth[name] = event.target.checked;
        } else {
            newPersonalHealth[name] = value;
        }

        setPersonalHealth(newPersonalHealth);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleAddTag = (value) => {
        updatePatient({
            variables: {
                userID: getPatientData?.patient?.user?.id,
                patientID: id,
                patientTag: value,
            },
        });
    };

    /*--------------- Emergency Form ---------------*/
    const [emergencyContact, setEmergencyContact] = useState({
        emergencyContractNumber: '',
        emergencyContractName: '',
        emergencyContractRelation: '',
    });

    const handleEmergencyContactChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        let newEmergencyContact = { ...emergencyContact };
        newEmergencyContact[name] = value;
        setEmergencyContact(newEmergencyContact);

        setErrors(errors.filter((error) => error.property !== name));
    };

    /*--------------- Get Patient ---------------*/
    const {
        data: getPatientData,
        error: getPatientError,
        loading: getPatientLoading,
        refetch: getPatientRefetch,
    } = useQuery(GET_PATIENT, {
        variables: { id: id },
        skip: !id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    const setPatient = () => {
        const {
            id,
            user,
            weight,
            height,
            drugAllergy,
            chronicDeseases1,
            chronicDeseases2,
            pregnant,
            smoker,
            drinker,
            familyMedicalRecord,
            emergencyContractNumber,
            emergencyContractName,
            emergencyContractRelation,
            patientTag,
            HNCode,
        } = getPatientData.patient;

        if (_.isNull(user)) {
            setIsDisabled(true);
        }

        setPatientData({
            id,
            patientTag,
            user,
        });

        setPersonalInfo({
            HNCode: HNCode || '',
            IDCardNumber: user?.IDCardNumber || '',
            fullname: user?.fullname || '',
            gender: user?.gender || '',
            email: user?.email || '',
            mobileNumber: user?.mobileNumber || '',
            birthday: user?.birthday || null,
        });

        setPersonalHealth({
            weight: weight || 0,
            height: height || 0,
            drugAllergy: drugAllergy || 'none',
            chronicDeseases1: chronicDeseases1 || 'none',
            pregnant: pregnant || false,
            smoker: smoker || false,
            drinker: drinker || false,
            chronicDeseases2: chronicDeseases2 || '',
            familyMedicalRecord: familyMedicalRecord || '',
            patientTag: patientTag || [],
        });

        setEmergencyContact({
            emergencyContractNumber: emergencyContractNumber || '',
            emergencyContractName: emergencyContractName || '',
            emergencyContractRelation: emergencyContractRelation || '',
        });
    };

    useEffect(() => {
        if (getPatientError) {
            dispatch(alertError(`Cannot get Client: ${getPatientError.message}`));
            history.goBack();
            return;
        }

        if (
            getPatientData &&
            (getPatientData.patient === null || getPatientData.patient.deactivated)
        ) {
            dispatch(alertError(`Client not found`));
            history.goBack();
            return;
        }

        if (getPatientData && getPatientData.patient) {
            setPatient();
        }
    }, [getPatientError, getPatientData]);

    /*--------------- Update Patient ---------------*/
    const [
        updatePatient,
        { data: updatePatientData, error: updatePatientError, loading: updatePatientLoading },
    ] = useMutation(UPDATE_PATIENT);

    useEffect(() => {
        if (updatePatientError) {
            dispatch(alertError(`Cannot update Client: ${updatePatientError.message}`));
        }
        if (updatePatientData) {
            dispatch(alertSuccess('Client updated'));
            getPatientRefetch();
        }
    }, [updatePatientError, updatePatientData]);

    const handleReset = () => {
        if (id) {
            setPatient();
            return;
        }

        setPersonalInfo({
            HNCode: '',
            IDCardNumber: '',
            fullname: '',
            gender: '',
            email: '',
            mobileNumber: '',
            birthday: null,
        });

        setPersonalHealth({
            weight: 0,
            height: 0,
            drugAllergy: 'none',
            chronicDeseases1: 'none',
            pregnant: false,
            smoker: false,
            drinker: false,
            chronicDeseases2: '',
            familyMedicalRecord: '',
            patientTag: [],
        });

        setEmergencyContact({
            emergencyContractNumber: '',
            emergencyContractName: '',
            emergencyContractRelation: '',
        });
    };

    const handleSave = () => {
        const newErrors = [];

        if (!checkID(personalInfo.IDCardNumber)) {
            newErrors.push({
                property: 'IDCardNumber',
                message: 'Please provide a valid ID Card Number.',
            });
        }

        if (personalInfo.fullname === '') {
            newErrors.push({
                property: 'fullname',
                message: 'Please provide a valid Fullname.',
            });
        }

        if (specialCharacters.test(personalInfo.fullname)) {
            newErrors.push({
                property: 'fullname',
                message: 'Fullname must not include special characters.',
            });
        }

        if (personalInfo.gender === '') {
            newErrors.push({
                property: 'gender',
                message: 'Please provide a valid gender.',
            });
        }

        if (!validEmail.test(personalInfo.email)) {
            newErrors.push({
                property: 'email',
                message: 'Please provide a valid Email.',
            });
        }

        if (!validPhone.test(personalInfo.mobileNumber)) {
            newErrors.push({
                property: 'mobileNumber',
                message: 'Please provide a valid Mobile Number.',
            });
        }

        if (!personalHealth.weight || personalHealth.weight == 0 || personalHealth.weight === '') {
            newErrors.push({
                property: 'weight',
                message: 'Please provide a valid weight.',
            });
        }

        if (!personalHealth.height || personalHealth.height == 0 || personalHealth.height === '') {
            newErrors.push({
                property: 'height',
                message: 'Please provide a valid height.',
            });
        }

        if (
            emergencyContact.emergencyContractNumber !== '' &&
            !validPhone.test(emergencyContact.emergencyContractNumber)
        ) {
            newErrors.push({
                property: 'emergencyContractNumber',
                message: 'Please provide a valid Contact Number.',
            });
        }

        if (!_.isEmpty(newErrors)) return setErrors(newErrors);

        updatePatient({
            variables: {
                userID: getPatientData?.patient?.user?.id,
                patientID: id,
                ...personalInfo,
                ...personalHealth,
                ...emergencyContact,
                height: parseFloat(personalHealth.height),
                weight: parseFloat(personalHealth.weight),
                birthday: personalInfo.birthday
                    ? dayjs(personalInfo.birthday).format('YYYY-MM-DD')
                    : null,
            },
        });
    };

    /*--------------- Primary Patient ---------------*/
    const [isPrimary, setIsPrimary] = useState(false);

    const {
        loading: getPrimaryLoading,
        error: getPrimaryError,
        data: getPrimaryData,
        refetch: getPrimaryRefetch,
    } = useQuery(GET_PRIMARY_PATIENTS, {
        variables: { id: user.staff.id },
        skip: !user.id && id,
    });

    useEffect(() => {
        if (getPrimaryError) {
            dispatch(alertError(`Cannot get Primary Client: ${getPrimaryError.message}`));
        }

        if (getPrimaryData && getPrimaryData.staff && getPrimaryData.staff.primaryPatients && id) {
            setIsPrimary(getPrimaryData.staff.primaryPatients.some((patient) => patient.id === id));
        }
    }, [getPrimaryData, getPrimaryError, id]);

    /*--------------- Deactivate Patient ---------------*/

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const handleRemovePatient = () => {
        setShowDialog(true);
    };

    const handleDeactivatePatient = async () => {
        setShowDialog(false);
        setLoading(true);

        try {
            const res = await removePatient({
                id,
                hospital: getPatientData.patient.hospital.id,
            });

            if (res.status === 200) {
                dispatch(alertSuccess('Client Removed'));
                history.goBack();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot Remove Client: ${error.response?.data?.message}`));
        }
        setLoading(false);
    };

    /*--------------- Update Primary Patient ---------------*/
    const handlePrimaryPatient = async (patientID) => {
        try {
            const staffID = user.staff.id;
            const res = await updatePrimaryPatient(staffID, patientID);

            if (res.status === 200) {
                dispatch(alertSuccess('Primary Client updated'));
                getPrimaryRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot update Primary Client: ${error.response?.data?.message}`));
        }
    };

    /*--------------- Get Patient Note ---------------*/
    const [notes, setNotes] = useState([]);

    const {
        loading: getPatientNoteLoading,
        error: getPatientNoteError,
        data: getPatientNoteData,
        refetch: getPatientNoteRefetch,
    } = useQuery(GET_NOTES, {
        variables: { id: id },
        skip: !id || !user.id,
    });

    useEffect(() => {
        if (getPatientNoteError) {
            dispatch(alertError(`Cannot get Primary Client: ${getPatientNoteError.message}`));
        }

        if (getPatientNoteData && getPatientNoteData.patientNotes) {
            const newNotes = getPatientNoteData.patientNotes.map((note) => ({
                ...note,
                mine: note.fromStaff?.id === user.staff.id,
            }));
            setNotes(
                newNotes.sort((a, b) => (dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1))
            );
        }
    }, [getPatientNoteData, getPatientNoteError]);

    /*--------------- Add Patient Note ---------------*/
    const [newNote, setNewNote] = useState('');

    const [
        createPatientNote,
        {
            data: createPatientNoteData,
            error: createPatientNoteError,
            loading: createPatientNoteLoading,
        },
    ] = useMutation(CREATE_PATIENT_NOTE);

    useEffect(() => {
        if (createPatientNoteError) {
            dispatch(alertError(`Cannot add note: ${createPatientNoteError.message}`));
        }
        if (createPatientNoteData) {
            dispatch(alertSuccess('Note Added'));
            getPatientNoteRefetch();
            setNewNote('');
        }
    }, [createPatientNoteError, createPatientNoteData]);

    const handleNewNoteChange = (e) => {
        setNewNote(e.target.value);
    };

    const handleAddNote = () => {
        createPatientNote({
            variables: {
                description: newNote,
                provider: user.staff.id,
                patient: id,
            },
        });
    };

    /*--------------- Update Patient Note ---------------*/
    const [
        updatePatientNote,
        {
            data: updatePatientNoteData,
            error: updatePatientNoteError,
            loading: updatePatientNoteLoading,
        },
    ] = useMutation(UPDATE_PATIENT_NOTE);

    useEffect(() => {
        if (updatePatientNoteError) {
            dispatch(alertError(`Cannot update note: ${updatePatientNoteError.message}`));
        }
        if (updatePatientNoteData) {
            dispatch(alertSuccess('Note Updated'));
            getPatientNoteRefetch();
        }
    }, [updatePatientNoteError, updatePatientNoteData]);

    const handleEditNote = (note) => {
        updatePatientNote({
            variables: {
                id: note.id,
                description: note.description,
            },
        });
    };

    /*--------------- Delete Patient Note ---------------*/
    const [
        deletePatientNote,
        {
            data: deletePatientNoteData,
            error: deletePatientNoteError,
            loading: deletePatientNoteLoading,
        },
    ] = useMutation(DELETE_PATIENT_NOTE);

    useEffect(() => {
        if (deletePatientNoteError) {
            dispatch(alertError(`Cannot delete note: ${deletePatientNoteError.message}`));
        }
        if (deletePatientNoteData) {
            dispatch(alertSuccess('Note Deleted'));
            getPatientNoteRefetch();
        }
    }, [deletePatientNoteError, deletePatientNoteData]);

    const handleDeleteNote = (noteID) => {
        deletePatientNote({
            variables: { id: noteID },
        });
    };

    /*--------------- History ---------------*/
    const [selectedMonth, setSelectedMonth] = useState(dayjs().get('month'));
    const [selectedYear, setSelectedYear] = useState(dayjs().get('year'));
    const [sessionsData, setSessionData] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const variables = useMemo(() => {
        const newVariables = {
            id: id,
            startDate: dayjs()
                .set({
                    month: selectedMonth,
                    year: selectedYear,
                })
                .startOf('month')
                .format('YYYY-MM-DD'),
            endDate: dayjs()
                .set({
                    month: selectedMonth,
                    year: selectedYear,
                })
                .endOf('month')
                .format('YYYY-MM-DD'),
        };

        return newVariables;
    }, [selectedMonth, selectedYear]);

    const {
        data: getHistoryData,
        error: getHistoryError,
        loading: getHistoryLoading,
    } = useQuery(GET_PATIENT_HISTORY, {
        variables: variables,
        skip: !id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getHistoryError) {
            dispatch(alertError(`Cannot get Client History: ${getHistoryError.message}`));
            return;
        }
        if (getHistoryData) {
            const filteredSessions = getHistoryData.sessions.filter(
                (session) =>
                    session.patient_summaries[0].joinTime !== null &&
                    session.patient_summaries[0].leaveTime !== null
            );

            if (searchValue === '') {
                setSessionData(filteredSessions);
            } else {
                const newHistoryData = [...filteredSessions].filter((record) => {
                    const date = new Date(record?.date);

                    const recordDate = `${date.getDate()} ${
                        MONTHS[date.getMonth()]
                    } ${date.getFullYear()}`.toLowerCase();

                    const recordTime = `${dayjs(record.patient_summaries[0].joinTime).format(
                        'HH:mm'
                    )} - ${dayjs(record.patient_summaries[0].leaveTime).format(
                        'HH:mm'
                    )}`.toLowerCase();

                    return (
                        recordDate.includes(searchValue.toLowerCase()) ||
                        recordTime.includes(searchValue.toLowerCase())
                    );
                });
                setSessionData(newHistoryData);
            }
        }
    }, [getHistoryError, getHistoryData, searchValue]);

    const handleSyncToMindGym = async () => {
        setLoading(true);

        try {
            const res = await syncUserMindGym({ userIdFriday: patientData.user.id });

            console.log('res', res);

            if (res.data.statusCode === 201) {
                dispatch(alertSuccess('Sync successfull'));
                getPatientRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            console.log('error', error.response);
            dispatch(alertError(`Cannot Sync user to MindGym: ${error.response?.data?.message}`));
        }
        setLoading(false);
    };
    useEffect(() => {
        const loading = setTimeout(() => {
            setPageLoading(false);
        }, 300);

        return () => {
            clearTimeout(loading);
        };
    }, []);

    if (pageLoading)
        return <div style={{ width: '100%', height: '100dvh', backgroundColor: 'white' }}></div>;

    return (
        <div
            style={{
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                columnGap: '25px',
                padding: '0 20px 50px',
            }}
        >
            {loading && <Progress />}
            <ConfirmDialog
                open={showDialog}
                title="Are you sure to remove client from lists ?"
                contentText={
                    <Span style={{ display: 'inline-block', marginBottom: '70px' }}>
                        After delete this account, client profile medical record
                    </Span>
                }
                contentTextColor="gray2"
                cancelText="Cancel"
                onCancel={handleCloseDialog}
                confirmText="Confirm"
                onConfirm={handleDeactivatePatient}
                confirmBackgroundColor="pink"
            />
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}>
                <div
                    style={{
                        paddingTop: '32px',
                        justifyContent: 'left',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <GoBackButton $text onClick={goBack} />
                </div>
                <StyledCard>
                    <StyledCardContent style={{ position: 'relative' }}>
                        {(getPatientLoading || updatePatientLoading || getHistoryLoading) && (
                            <Progress />
                        )}
                        <PatientAPCard
                            data={patientData}
                            $avatarSize="64px"
                            skippedButton={!isEditing}
                            showDiagnosis
                            onAddPrimaryPatient={handlePrimaryPatient}
                            isPrimary={isPrimary}
                            loading={getPrimaryLoading}
                            disabledStarButton={getPrimaryError}
                            disabled={isDisabled}
                        />
                        <StyledTabs
                            value={selectedTab}
                            onChange={tabsChangeHandler}
                            indicatorColor="primary"
                            TabIndicatorProps={{ style: { height: '4px' } }}
                        >
                            <PatientDetailTab label="Client Profile" $active={selectedTab === 0} />
                            <PatientDetailTab
                                label="Sessions History"
                                $active={selectedTab === 1}
                            />
                        </StyledTabs>
                        {selectedTab === 0 && (
                            <PersonalInfoForm
                                mindGymUser={patientData.user.uuid}
                                data={personalInfo}
                                onChange={handlePersonalChange}
                                onDateChange={handleDateChange}
                                errors={errors}
                                disabledHNCode={!isEditing}
                                disabledIDCardNumber={!isEditing}
                                disabledEmail={!isEditing}
                                disabledPhone={!isEditing}
                                disabledBirthday={!isEditing}
                                radioRow
                                disabledForm={isDisabled}
                            />
                        )}
                        {selectedTab === 1 && (
                            <AppointmentHistory
                                data={sessionsData}
                                selectedMonth={selectedMonth}
                                setSelectedMonth={setSelectedMonth}
                                selectedYear={selectedYear}
                                setSelectedYear={setSelectedYear}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                            />
                        )}
                    </StyledCardContent>
                </StyledCard>
                {selectedTab === 0 && (
                    <Fragment>
                        <StyledCard>
                            <StyledCardContent style={{ position: 'relative' }}>
                                {(getPatientLoading || updatePatientLoading) && <Progress />}
                                <PersonalHealthForm
                                    data={personalHealth}
                                    onChange={handlePersonalHealthChange}
                                    minRows={2}
                                    onAddTag={handleAddTag}
                                    errors={errors}
                                    disabledForm={isDisabled}
                                />
                            </StyledCardContent>
                        </StyledCard>
                        <StyledCard>
                            <StyledCardContent style={{ position: 'relative' }}>
                                {(getPatientLoading || updatePatientLoading) && <Progress />}
                                <EmergencyForm
                                    data={emergencyContact}
                                    onChange={handleEmergencyContactChange}
                                    errors={errors}
                                    disabledForm={isDisabled}
                                />
                            </StyledCardContent>
                        </StyledCard>
                    </Fragment>
                )}
                {selectedTab === 0 && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
                            {patientData?.user?.uuid && (
                                <ContainedButton
                                    $width="200px"
                                    $height="32px"
                                    $backgroundColor="green9"
                                    disabled={
                                        getPatientLoading ||
                                        updatePatientLoading ||
                                        loading ||
                                        isDisabled
                                    }
                                    style={{
                                        pointerEvents: 'none',
                                        cursor: 'default',
                                    }}
                                >
                                    <CheckIcon />
                                    <Text $color="white" style={{ marginLeft: '5px' }}>
                                        Sync Completed
                                    </Text>
                                </ContainedButton>
                            )}
                            {!patientData?.user?.uuid && patientData?.user?.confirmed && (
                                <OutlinedButton
                                    $width="229px"
                                    $height="32px"
                                    $color="primaryBlue"
                                    onClick={handleSyncToMindGym}
                                    disabled={
                                        getPatientLoading ||
                                        updatePatientLoading ||
                                        loading ||
                                        isDisabled
                                    }
                                >
                                    <SyncIcon />
                                    <Text $color="primaryBlue" style={{ marginLeft: '5px' }}>
                                        Sync with MindGym
                                    </Text>
                                </OutlinedButton>
                            )}

                            <OutlinedButton
                                $width="183px"
                                $height="32px"
                                $color="pink"
                                onClick={handleRemovePatient}
                                disabled={
                                    getPatientLoading ||
                                    updatePatientLoading ||
                                    loading ||
                                    isDisabled
                                }
                            >
                                Remove from list
                            </OutlinedButton>
                        </div>
                        <div>
                            <OutlinedButton
                                $width="96px"
                                $height="32px"
                                onClick={handleReset}
                                disabled={getPatientLoading || updatePatientLoading || isDisabled}
                            >
                                Reset
                            </OutlinedButton>
                            <ContainedButton
                                $width="96px"
                                $height="32px"
                                style={{ marginLeft: '15px' }}
                                onClick={handleSave}
                                disabled={getPatientLoading || updatePatientLoading || isDisabled}
                            >
                                Save
                            </ContainedButton>
                        </div>
                    </div>
                )}
            </div>
            <div
                style={{
                    width: '100%',
                    paddingTop: '20px',
                }}
            >
                <PatientNoteCard
                    data={notes}
                    value={newNote}
                    onChange={handleNewNoteChange}
                    handleAddNote={handleAddNote}
                    handleEditNote={handleEditNote}
                    handleDeleteNote={handleDeleteNote}
                    loading={
                        getPatientNoteLoading ||
                        createPatientNoteLoading ||
                        updatePatientNoteLoading ||
                        deletePatientNoteLoading
                    }
                    disabled={isDisabled}
                />
            </div>
        </div>
    );
};

PatientDetail.propTypes = {
    history: PropTypes.object,
    match: PropTypes.object,
};

export default PatientDetail;

const StyledTabs = withStyles({
    root: {
        marginTop: '16px',
        marginBottom: '16px',
    },
    flexContainer: {
        columnGap: '45px',
    },
})(Tabs);
